// TestBookMCQTest.jsx
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import './styles/MCQPractice.css';

const TestBookMCQTest = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Important - useEffect to stop infinite rendering
    }, [location.state.setId]);

    const [setId, setSetId] = useState(location.state.setId);
    const [setTitle, setSetTitle] = useState(location.state.setTitle);
    const [seriesId, setSeriesId] = useState(location.state.seriesId);
    const [seriesTitle, setSeriesTitle] = useState(location.state.seriesTitle);
    const [examId, setExamId] = useState(location.state.examId);
    const [examTitle, setExamTitle] = useState(location.state.examTitle);
    const [examType, setType] = useState(location.state.examType);
    const [timeLimitInMin, setTimeLimitInMin] = useState(location.state.timeLimitInMin);
    const [isRepeatPractice, setIsRepeatPractice] = useState(location.state.isRepeatPractice);

    const [practiceTitle, setPracticeTitle] = useState("TestBook - MCQ Practice");
    const [repeatPracticeId, setRepeatPracticeId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [timer, setTimer] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    // const [selectedOptions, setSelectedOptions] = useState(Array(10).fill(null));
    const [selectedOptions, setSelectedOptions] = useState(Array(questions.length).fill(null));


    const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
    const [fullMarks, setFullMarks] = useState(0);
    const [totalMarksObtained, setTotalMarksObtained] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [responseData, setResponseData] = useState(null);

    useEffect(() => {
        if (!authState.isLoggedIn) return;
        fetchQuestions();
    }, [authState]);


    const fetchQuestions = useCallback(() => {
        if (!setId) return;
        const config = {
            url: `/questionmaster/forttbsetid`,
            method: "post",
            headers: { Authorization: authState.token },
            data: {setId: setId}
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                // console.log("Question List Response:", response);
                if (response && Array.isArray(response.questionlist)) {
                    setQuestions(response.questionlist);
                } else {
                    console.error("Invalid response format: Assignment not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching Assignments:", error);
            });
    }, [fetchData, setId]);

    useEffect(() => {
        // console.log("Questions: ", questions);
    }, [questions]);

    useEffect(() => {
        if (submitted) {
            return;
        }
        setStartTime(new Date());
        const intervalId = setInterval(() => {
            setTimer(prevTimer => prevTimer + 1);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [submitted]);

    const handleOptionChange = (index, option) => {
        const newSelectedOptions = [...selectedOptions];
        newSelectedOptions[index] = option;
        setSelectedOptions(newSelectedOptions);
    };

    const handleSubmit = () => {
        if (submitted) {
            return;
        }
        const answeredQuestions = selectedOptions.filter(option => option !== null);
        if (answeredQuestions.length !== questions.length) {
            alert("Complete your test. Give all the answers before submitting it.");
            return;
        }
        setEndTime(new Date());
        setSubmitted(true);
        showAnswers();
    };

    const showAnswers = () => {
        const correctCount = questions.reduce((count, question, index) => {
            const correctAnswer = question.mcqCorrectAnswer;
            const userAnswer = selectedOptions[index];
            if (userAnswer === correctAnswer) {
                return count + 1;
            }
            return count;
        }, 0);

        const totalQuestions = questions.length;

        let totalMarksObtained = 0;
        let fullMarks = 0;
        let totalCorrectAnswers = 0;

        const answeredQuestions = questions.map((question, index) => {
            const correctAnswer = question.mcqCorrectAnswer;
            const userAnswer = selectedOptions[index];
            const isCorrect = userAnswer === correctAnswer;
            const correctAnswerPoints = question.correctAnswerPoints;
            const wrongAnswerPoints = question.wrongAnswerPoints;
            const isCorrectAnswer = isCorrect ? 1 : 0;
            totalCorrectAnswers += isCorrectAnswer;
            const marksObtained = isCorrect ? question.correctAnswerPoints : question.wrongAnswerPoints;
            totalMarksObtained += marksObtained;
            fullMarks += question.correctAnswerPoints;
            return { question, correctAnswer, userAnswer, marksObtained, correctAnswerPoints, wrongAnswerPoints };
        });
        setAnswers(answeredQuestions);
        setTotalCorrectAnswers(totalCorrectAnswers);
        setFullMarks(fullMarks);
        setTotalMarksObtained(totalMarksObtained);
        const calculatedPercentage = (totalMarksObtained / fullMarks) * 100;
        setPercentage(calculatedPercentage);
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSavePractice = () => {
        savePracticeToDB();
    };

    const savePracticeToDB = async () => {
        const userId = authState.user._id;
        const numberOfQuestions = questions.length;
        const totalIncorrectAnswers = numberOfQuestions - totalCorrectAnswers;
        const requestData = {
            setQuestionType: "MCQ",
            userId: userId,
            setId: setId,
            setTitle: setTitle,
            seriesId: seriesId,
            seriesTitle: seriesTitle,
            examId: examId,
            examTitle: examTitle,
            examType: examType,
            practiceTitle: practiceTitle,
            isRepeatPractice: isRepeatPractice,
            repeatPracticeId: repeatPracticeId,
            numberOfQuestions: numberOfQuestions,
            correctAnswers: totalCorrectAnswers,
            incorrectAnswers: totalIncorrectAnswers,
            datePracticed: startTime,
            practiceTimeStarted: startTime,
            practiceTimeEnded: endTime,
            marksObtained: totalMarksObtained,
            percentage: percentage,
            totalMarksObtained: totalMarksObtained,
            fullMarks: fullMarks,
            answers: answers,
            remarks: "",
            tags: "",
            practiceLocation: "",
            mentorName: "",
            mentorRemarks: "",
            selfFeedback: "",
            think5whysRcaId: "",
            locationIP: "",
            hasExplanation: 0,
            isActive: 1,
            isDeleted: 0,
            createBy: userId,
            timeTaken: timer,
            timeLimit: timeLimitInMin,
            mcqPracticeCode: generatePracticeCode(),
        };
        // console.log('Practice data to be saved:', requestData);
        try {
            const config = { url: `/ttbsavepractice/ttbsave`, method: "post", data: requestData, headers: { Authorization: authState.token } };
            await fetchData(config);
            navigate('/testbook-historymcq');
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
        }
    };

    const generatePracticeCode = () => {
        const now = new Date();
      
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0'); 
        const yyyy = now.getFullYear();
        const hh = String(now.getHours()).padStart(2, '0');
        const min = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        const practiceCode = `${dd}${mm}${yyyy}${hh}${min}${ss}`;
      
        return practiceCode;
    };

    const getOptionValue = (options, key) => {
        return options[key];
    };

    const isSubmitDisabled = selectedOptions.includes(null);

    const formatTime = (timeInSeconds) => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = timeInSeconds % 60;

        return `${hours}Hr ${minutes}Min ${seconds}Sec`;
    };

    return (
        <MainLayout>
            <div className="bg-gray-900 py-4 px-8 shadow-md mt-12">
                {/* <h3 className="text-2xl font-semibold text-yellow-400 border-b-2 border-yellow-500 pb-2">Think5Whys MCQ Practice Test</h3> */}
            {/* </div>
            <div className="practice-test-container max-w-4xl mx-auto mt-6 p-6 bg-gray-800 rounded-lg shadow-lg"> */}
                <h1 className="text-3xl font-bold text-yellow-300 mb-6">MCQ Practice Test</h1>
                <p className="text-lg text-white mb-2">Welcome, {authState.user?.name || 'Guest'}</p>
                <p className="text-lg text-white mb-2">Practice Set: <span className="font-semibold">{setTitle}</span></p>
                <p className="text-lg text-white mb-2">Practice Series: <span className="font-semibold">{seriesTitle}</span></p>
                <p className="text-lg text-white mb-2">Exam: <span className="font-semibold">{examTitle} - {examType}</span></p>
                <p className="text-lg text-white mb-2">Start Time: <span className="font-semibold">{startTime?.toLocaleString()}</span></p>
                <p className="text-lg text-yellow-400 mb-6">Time Limit: <span className="font-semibold">{timeLimitInMin} Minutes</span></p>

                {questions.map((question, index) => (
                    <div key={question._id} className="question-item bg-gray-700 p-4 mb-4 rounded-md shadow-md">
                        <p className="text-lg text-white mb-2">Question {index + 1}</p>
                        <p className="text-base text-white mb-2">Points: <span className="text-green-400">{question.correctAnswerPoints}</span> / <span className="text-red-400">{question.wrongAnswerPoints}</span></p>
                        <p className="text-base text-white mb-4">{question.question}</p>
                        {question.isImageQuestion === 1 && (
                            <img src={`data:image/png;base64,${question.questionImage}`} alt={"No Image" + question.question} />
                        )}
                        <form>
                            <div>
                                <input
                                    type="radio"
                                    id={`${question._id}-A`}
                                    name={`question_${index}`}
                                    value="A"
                                    onChange={() => handleOptionChange(index, 'A')}
                                />
                                <label htmlFor={`${question._id}-A`}>{question.mcqOptions[0].A}</label>
                            </div>
                            <div>
                                <input
                                    type="radio"
                                    id={`${question._id}-B`}
                                    name={`question_${index}`}
                                    value="B"
                                    onChange={() => handleOptionChange(index, 'B')}
                                />
                                <label htmlFor={`${question._id}-B`}>{question.mcqOptions[0].B}</label>
                            </div>
                            <div>
                                <input
                                    type="radio"
                                    id={`${question._id}-C`}
                                    name={`question_${index}`}
                                    value="C"
                                    onChange={() => handleOptionChange(index, 'C')}
                                />
                                <label htmlFor={`${question._id}-C`}>{question.mcqOptions[0].C}</label>
                            </div>
                            <div>
                                <input
                                    type="radio"
                                    id={`${question._id}-D`}
                                    name={`question_${index}`}
                                    value="D"
                                    onChange={() => handleOptionChange(index, 'D')}
                                />
                                <label htmlFor={`${question._id}-D`}>{question.mcqOptions[0].D}</label>
                            </div>
                        </form>
                    </div>
                ))}
                <button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                >
                    Submit
                </button>
                {submitted && (
                    <>
                    <div className="submission-details text-white mt-8">
                    <p className="text-lg">Your Practice Test Is Over, {authState.user?.name || 'Guest'}.</p>
                    <p>Start Time: {startTime?.toLocaleString()}</p>
                    <p>End Time: {endTime?.toLocaleString()}</p>
                    <p>Time Taken: {formatTime(timer)}</p>
                    </div>
                    
                    <div className="additional-stats text-yellow-300 mt-6">
                    <p>Number of Questions: {questions.length}</p>
                    <p>Correct Answers: {answers.filter(answer => answer.userAnswer === answer.correctAnswer).length}</p>
                    <p>Incorrect Answers: {answers.filter(answer => answer.userAnswer !== answer.correctAnswer).length}</p>
                    <p>Full Marks: {fullMarks}</p>
                    <p>Marks Obtained: {totalMarksObtained}</p>
                    <p>Percent: {percentage}%</p>
                    </div>
                    
                    <div className="progress-container bg-gray-700 rounded-full h-8 overflow-hidden mt-6">
                    <div
                        className="progress bg-yellow-500 h-full text-center text-white font-bold"
                        style={{ width: `${percentage}%` }}
                    >
                        {`${percentage}%`}
                    </div>
                    </div>
                    
                    {answers.map((answer, index) => (
                    <div key={index} className="answer-item bg-gray-700 p-4 mb-4 mt-4 rounded-md shadow-md">
                        <p className="text-lg text-yellow-300 mb-2">Question {index + 1}: {answer.question.question}</p>
                        <p className="text-base text-white mb-2">
                        Your Answer: <span className={answer.userAnswer === answer.correctAnswer ? 'text-green-400' : 'text-red-400'}>{answer.userAnswer} ({getOptionValue(answer.question.mcqOptions[0], answer.userAnswer)})</span>
                        </p>
                        {answer.userAnswer !== answer.correctAnswer && (
                        <p className="text-base text-red-400 mb-2">
                            Correct Answer: <span className="text-green-400">{answer.correctAnswer} ({getOptionValue(answer.question.mcqOptions[0], answer.correctAnswer)})</span>
                        </p>
                        )}
                        <p className="text-base text-white">Marks Obtained: {answer.marksObtained}</p>
                        <p className="text-sm text-gray-400">[Correct Answer: {answer.correctAnswerPoints} | Wrong Answer: {answer.wrongAnswerPoints}]</p>
                    </div>
                    ))}
                    
                    <button className="save-practice-button bg-green-500 text-gray-900 font-bold py-2 px-6 rounded-lg shadow-md hover:bg-green-600 mt-6" onClick={handleSavePractice}>
                        Save My Practice
                    </button>
                </>
                )}
            </div>
        </MainLayout>
    );
};

export default TestBookMCQTest;
